import React, { useState, useEffect, useRef } from 'react';
import { saveQuizResult } from '../../utils/quizService';


export default function QuizCard({ quizSession, questions = [], onQuizComplete, onStudySessionComplete, onClose, isLastQuiz, onFinalQuizComplete, onQuizResultSaved }) {
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [showExplanation, setShowExplanation] = useState(null);
  const [showHint, setShowHint] = useState(false);
  const [timeLeft, setTimeLeft] = useState(quizSession.timeLimit);
  const [showAnswer, setShowAnswer] = useState(false);
  const [error, setError] = useState(null);
  const [quizData, setQuizData] = useState([]);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef();

  // Check if this is a single-question quiz or the final quiz
  const isSingleQuiz = quizSession.questions === 1;
  const isFinalQuiz = !!quizSession.isFinal;

  // Convert questions to the format expected by QuizCard
  useEffect(() => {
    if (questions && questions.length > 0) {
      setLoading(true);
      try {
        const convertedQuestions = questions.map(q => ({
          question: q.question,
          choices: Object.values(q.choices),
          correct: Object.keys(q.choices).indexOf(q.correct_answer),
          explanation: q.explanation,
          hint: q.hint || q.explanation
        }));
        setQuizData(convertedQuestions.slice(0, quizSession.questions));
        setError(null);
      } catch (err) {
        setError('Failed to process quiz questions');
        console.error('Error processing quiz data:', err);
      } finally {
        setLoading(false);
      }
    } else {
      setError('No questions available. Please try again.');
      setLoading(false);
    }
  }, [questions, quizSession.questions]);

  // Local timer for frontend testing
  useEffect(() => {
    if (quizData.length > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft((t) => {
          if (t <= 1) {
            clearInterval(timerRef.current);
            setShowAnswer(true);
            return 0;
          }
          return t - 1;
        });
      }, 1000);

      return () => clearInterval(timerRef.current);
    }
  }, [quizData]);

  // Show hint once the configured number of seconds remain
  useEffect(() => {
    if (timeLeft === quizSession.hintAtSecRemaining && !showHint) {
      setShowHint(true);
    }
  }, [timeLeft, showHint, quizSession.hintAtSecRemaining]);

  // Reset state when quiz session changes
  useEffect(() => {
    setCurrent(0);
    setSelected(null);
    setSubmitted(false);
    setAnswers([]);
    setShowResults(false);
    setShowExplanation(null);
    setShowHint(false);
    setShowAnswer(false);
    setTimeLeft(quizSession.timeLimit);
    setError(null);
  }, [quizSession.id]);

  // Format time as mm:ss
  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  const timeString = `${minutes.toString().padStart(2, '0')}:${seconds
    .toString()
    .padStart(2, '0')}`;

  const handleSelect = (i) => {
    if (!submitted && !showAnswer) setSelected(i);
  };

  const handleSubmit = async () => {
    try {
      setSubmitted(true);
      setAnswers((prev) => {
        const updated = [...prev];
        updated[current] = selected;
        return updated;
      });
    } catch (err) {
      console.error('Error submitting answer:', err);
    }
  };

  const handleNext = () => {
    setSubmitted(false);
    setSelected(null);
    if (current < quizData.length - 1) {
      setCurrent((c) => c + 1);
    } else {
      setShowResults(true);
    }
  };

  // Save quiz results to backend when quiz is completed
  useEffect(() => {
    if (showResults && quizData.length > 0) {
      const saveResults = async () => {
        try {
          // Calculate results for each question
          for (let i = 0; i < quizData.length; i++) {
            const question = quizData[i];
            const userAnswer = answers[i];
            const isCorrect = userAnswer === question.correct;
            
            // Calculate score (10 points per correct answer)
            const scoreAwarded = isCorrect ? 10 : 0;

             // Get answer texts
            const studentAnswerText = userAnswer !== null && userAnswer !== undefined 
              ? question.choices[userAnswer] || 'No answer provided'
              : 'No answer provided';
            const correctAnswerText = question.choices[question.correct] || '';

            // Prepare quiz result data
            const quizResult = {
              quiz_id: `${quizSession.name.replace(/\s+/g, '_').toLowerCase()}_${Date.now()}_q${i + 1}`,
              subject_name: quizSession.subjectName || 'General',
              topic_name: quizSession.topicName || 'Reading Session',
              title: `${quizSession.name} - Question ${i + 1}`,
              question_text: question.question || '',
              options: question.choices || [],
              student_answer: studentAnswerText,
              correct_answer: correctAnswerText,
              isCorrect: isCorrect,
              scoreAwarded: scoreAwarded
            };

            console.log(`💾 Saving result for question ${i + 1}:`, quizResult);

            // Save to backend
            const result = await saveQuizResult(quizResult);
            
            if (result.success) {
              console.log(`✅ Question ${i + 1} result saved successfully`);
              // Trigger refresh of subject scores
              if (onQuizResultSaved) {
                onQuizResultSaved();
              }
            } else {
              console.error(`❌ Failed to save question ${i + 1} result:`, result.error);
            }
          }

          // Calculate and save overall quiz stats
          const correctCount = quizData.reduce(
            (acc, q, i) => acc + (answers[i] === q.correct ? 1 : 0),
            0
          );
          const totalScore = correctCount * 10;

          console.log(`📊 Quiz completed: ${correctCount}/${quizData.length} correct, Score: ${totalScore}`);
          
        } catch (error) {
          console.error('❌ Error saving quiz results:', error);
        }
      };

      saveResults();
    }
  }, [showResults, quizData, answers, quizSession.name]);

  const handleContinueToStudy = async () => {
    try {
      if (isFinalQuiz) {
        // For Final Quiz, go to dashboard
        onFinalQuizComplete();
      } else {
        // For all other quizzes, close the overlay and return to reading
        onClose();
      }
    } catch (err) {
      console.error('Error completing quiz:', err);
    }
  };

  const handleStudySessionComplete = () => {
    onStudySessionComplete();
  };

  // Loading state
  if (loading) {
    return (
      <div className="quiz-card-container">
        <div className="quiz-loading">
          <div className="quiz-loading-text">Loading quiz questions...</div>
          <div className="quiz-loading-spinner"></div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="quiz-card-container">
        <div className="quiz-error">
          <div className="quiz-error-text">{error}</div>
          <button
            className="quiz-retry-button app-btn"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Results UI
  if (showResults) {
    const correctCount = quizData.reduce(
      (acc, q, i) => acc + (answers[i] === q.correct ? 1 : 0),
      0
    );
    return (
      <div className="quiz-results-container">
        <div className="quiz-results-header">
          <div className="quiz-results-title">
            {quizSession.name} Completed — Score: {correctCount} / {quizData.length}
          </div>
        </div>
        <div className="quiz-results-questions">
          {quizData.map((q, i) => (
            <div key={i} className="quiz-result-item">
              <div className="quiz-result-question">
                Q{i + 1}: {q.question}
              </div>
              <div className="quiz-result-answer">
                <span>Your answer:</span>{' '}
                <span className={answers[i] === q.correct ? 'correct' : 'incorrect'}>
                  {typeof answers[i] === 'number'
                    ? q.choices[answers[i]]
                    : <span className="no-answer">No answer</span>}
                </span>
              </div>
              <div className="quiz-result-correct">
                <span>Correct answer:</span>{' '}
                <span className="correct-answer">{q.choices[q.correct]}</span>
              </div>
              <div className="quiz-result-explanation">
                <span>Explanation:</span> {q.explanation}
              </div>
            </div>
          ))}
        </div>
        <button
          className="quiz-continue-button app-btn"
          onClick={handleContinueToStudy}
        >
          {isFinalQuiz ? 'Go to Dashboard' : 'Go on Reading'}
        </button>
      </div>
    );
  }

  if (!quizData || quizData.length === 0 || !quizData[current]) {
    return (
      <div className="quiz-card-container">
        <div className="quiz-error">
          <div className="quiz-error-text">No quiz questions available.</div>
          <button className="quiz-retry-button app-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    );
 }


  // Main Quiz UI
  return (
    <div className="quiz-main-container">
      <div className="quiz-content-wrapper">
        {/* Large Question Number for final quiz */}
        {isFinalQuiz && (
          <div className="quiz-question-number">
            <span>Q{current + 1}</span>
          </div>
        )}
        
        {/* Quiz Card */}
        <div className="quiz-card">
          {/* Quiz Session Header */}
          <div className="quiz-header">
            <h1 className="quiz-title">{quizSession.name}</h1>
            {quizData.length > 1 && (
              <p className="quiz-progress">Question {current + 1} of {quizData.length}</p>
            )}
          </div>
          
          
          <h2 className="quiz-question">{quizData[current].question}</h2>
          
          {/* Show Answer if Time Expired */}
          {showAnswer && (
            <div className="quiz-time-up">
              <div className="quiz-time-up-title">Time's Up! Here's the Answer:</div>
              <div className="quiz-time-up-answer">{quizData[current].choices[quizData[current].correct]}</div>
              <div className="quiz-time-up-explanation">{quizData[current].explanation}</div>
            </div>
          )}

          {/* Show Hint if Available */}
          {showHint && !showAnswer && (
            <div className="quiz-hint">
              <div className="quiz-hint-title">💡 Hint:</div>
              <div className="quiz-hint-text">{quizData[current].hint}</div>
            </div>
          )}

          <div className="quiz-choices">
            {quizData[current].choices.map((c, i) => {
              let className = 'quiz-choice';
              
              if (submitted || showAnswer) {
                if (i === quizData[current].correct) {
                  className += ' quiz-choice-correct';
                }
                if (i === selected && i !== quizData[current].correct) {
                  className += ' quiz-choice-incorrect';
                }
              } else if (i === selected) {
                className += ' quiz-choice-selected';
              }
              
              return (
                <label
                  key={i}
                  className={className}
                  onClick={() => handleSelect(i)}
                >
                  <input
                    type="radio"
                    checked={selected === i}
                    onChange={() => handleSelect(i)}
                    disabled={submitted || showAnswer}
                    className="quiz-choice-radio"
                  />
                  <span>{c}</span>
                </label>
              );
            })}
          </div>
          
          {/* Button */}
          {!showAnswer && (
            <button
              className={`quiz-submit-button app-btn ${
                !submitted
                  ? selected === null
                    ? 'disabled'
                    : 'enabled'
                  : 'next'
              }`}
              disabled={!submitted && selected === null}
              onClick={!submitted ? handleSubmit : handleNext}
            >
              {!submitted
                ? 'Check Answer'
                : current < quizData.length - 1
                ? 'Next Question'
                : 'Finish Quiz'}
            </button>
          )}
          
          {/* Continue Button when Time Expired */}
          {showAnswer && (
            <button
              className="quiz-continue-button app-btn"
              onClick={handleContinueToStudy}
            >
              {isFinalQuiz ? 'Go to Dashboard' : 'Go on Reading'}
            </button>
          )}
        </div>
        
      </div>

      {/* Progress Bar - Only show for Final Quiz */}
      {isFinalQuiz && (
        <div className="quiz-progress-bar">
          <div className="quiz-progress-label">You got this!</div>
          <div className="quiz-progress-container">
            <div className="quiz-progress-track">
              <div
                className="quiz-progress-fill"
                style={{ width: `${((current + 1) / quizData.length) * 100}%` }}
              />
            </div>
            <div className="quiz-progress-text">
              Q{current + 1} / {quizData.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}